import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import WhatIsThisTip from '../components/admin/WhatIsThisTip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2, Database, Brain, Users, Users2, FileText, Palette, Sliders, Save, DollarSign, Crown, Shield, Lock, UserCheck, Image, Video, Monitor, BookOpen, ShieldAlert, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import KnowledgeBaseManager from '../components/KnowledgeBaseManager';
import TextEntriesManager from '../components/TextEntriesManager';
import AIKnowledgeHealthAlert from '../components/AIKnowledgeHealthAlert';
import EducationStandardsManager from '../components/EducationStandardsManager';

const AdminPortal = () => {
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [activeTab, setActiveTab] = useState('knowledge');
 
 // Knowledge Base
 const [knowledgeBase, setKnowledgeBase] = useState([]);
 const [showKnowledgeDialog, setShowKnowledgeDialog] = useState(false);
 const [editingKnowledge, setEditingKnowledge] = useState(null);
 const [newKnowledge, setNewKnowledge] = useState({ title: '', content: '', category: 'General', enabled: true });
 
 // Partners
 const [partners, setPartners] = useState([]);
 const [showPartnerDialog, setShowPartnerDialog] = useState(false);
 const [editingPartner, setEditingPartner] = useState(null);
 const [newPartner, setNewPartner] = useState({ name: '', logo: '', description: '', region: '', link: '', featured: false });
 
 // Products
 const [products, setProducts] = useState([]);
 const [showProductDialog, setShowProductDialog] = useState(false);
 const [editingProduct, setEditingProduct] = useState(null);
 const [newProduct, setNewProduct] = useState({ product_name: '', category: 'Fruit', water_content: 0, pre_freeze_instructions: '', temp_range: '', pressure_range: '', primary_dry_time: '', secondary_dry_time: '', notes: '' });
 
 // Videos
 const [videos, setVideos] = useState([]);
 const [showVideoDialog, setShowVideoDialog] = useState(false);
 const [editingVideo, setEditingVideo] = useState(null);
 const [newVideo, setNewVideo] = useState({ title: '', description: '', url: '', category: 'Beginner', is_pro_only: false });
 
 // Store Items
 const [storeItems, setStoreItems] = useState([]);
 const [showStoreDialog, setShowStoreDialog] = useState(false);
 const [editingStore, setEditingStore] = useState(null);
 const [newStoreItem, setNewStoreItem] = useState({ name: '', description: '', price: 0, msrp: 0, on_sale: false, image: '', company_logo: '', category: 'Accessories', machine_name: '', external_url: '' });
 
 // Currency Settings
 const [currencySettings, setCurrencySettings] = useState(null);
 const [usdRate, setUsdRate] = useState(0.74);
 const [rateHistory, setRateHistory] = useState([]);
 const [showRateHistory, setShowRateHistory] = useState(false);
 
 // Consulting Requests
 const [consultingRequests, setConsultingRequests] = useState([]);
 
 // App Settings
 const [settings, setSettings] = useState(null);
 const [settingsModified, setSettingsModified] = useState(false);
 
 // Subscription Tiers
 const [subscriptionTiers, setSubscriptionTiers] = useState([]);
 const [showTierDialog, setShowTierDialog] = useState(false);
 const [editingTier, setEditingTier] = useState(null);
 const [newTier, setNewTier] = useState({ 
 name: '', 
 display_name: '', 
 monthly_price: 0, 
 annual_price: 0, 
 description: '', 
 features: [], 
 enabled: true, 
 order: 1, 
 popular: false 
 });
 const [newFeature, setNewFeature] = useState('');
 
 // User Subscriptions
 const [userSubscriptions, setUserSubscriptions] = useState([]);
 
 // Moderators & Permissions
 const [moderators, setModerators] = useState([]);
 const [allUsers, setAllUsers] = useState([]);
 const [permissions, setPermissions] = useState({ permissions: {}, presets: {} });
 const [showModeratorDialog, setShowModeratorDialog] = useState(false);
 const [newModerator, setNewModerator] = useState({ user_email: '', permissions: [], areas: [], notes: '' });
 
 // Media & Ads Management
 const [mediaAssets, setMediaAssets] = useState([]);
 const [adCampaigns, setAdCampaigns] = useState([]);
 const [adLocations, setAdLocations] = useState([]);
 const [adAnalytics, setAdAnalytics] = useState({});
 const [showMediaDialog, setShowMediaDialog] = useState(false);
 const [showCampaignDialog, setShowCampaignDialog] = useState(false);
 const [editingMedia, setEditingMedia] = useState(null);
 const [editingCampaign, setEditingCampaign] = useState(null);
 const [newMediaAsset, setNewMediaAsset] = useState({
 name: '', description: '', media_type: 'image', file_url: '', embed_code: '', 
 embed_url: '', alt_text: '', width: null, height: null, metadata: {}
 });
 const [newCampaign, setNewCampaign] = useState({
 name: '', description: '', media_asset_id: '', location: 'dashboard_banner', 
 priority: 'medium', target_user_roles: ['free', 'tier_1', 'tier_2', 'tier_3'], 
 exclude_admin: true, start_date: null, end_date: null, click_url: '', 
 display_order: 0, max_impressions: null, custom_title: '', custom_description: '', custom_cta_text: ''
 });

 // Design System Management
 const [designSystems, setDesignSystems] = useState([]);
 const [activeDesignSystem, setActiveDesignSystem] = useState(null);
 const [themePresets, setThemePresets] = useState([]);
 const [showDesignDialog, setShowDesignDialog] = useState(false);
 const [showColorPicker, setShowColorPicker] = useState(false);
 const [editingDesignSystem, setEditingDesignSystem] = useState(null);
 const [newDesignSystem, setNewDesignSystem] = useState({
 name: '', theme_preset: 'custom', colors: {}, typography: {}, spacing: {}, components: {}, branding: {}
 });
 const [previewMode, setPreviewMode] = useState(false);
 
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 if (user?.role !== 'admin') {
 navigate('/dashboard');
 return;
 }
 fetchAllData();
 }, []);

 const fetchAllData = async () => {
 try {
 const results = await Promise.allSettled([
 axios.get(`${API}/admin/ai-knowledge`),
 axios.get(`${API}/partners`),
 axios.get(`${API}/freeze-dry-database`),
 axios.get(`${API}/admin/videos/all`),
 axios.get(`${API}/store`),
 axios.get(`${API}/admin/consulting-requests`),
 axios.get(`${API}/settings`),
 axios.get(`${API}/admin/subscription-tiers/all`),
 axios.get(`${API}/admin/subscriptions`),
 axios.get(`${API}/admin/moderators`),
 axios.get(`${API}/admin/users`),
 axios.get(`${API}/admin/permissions`),
 axios.get(`${API}/admin/media-assets`),
 axios.get(`${API}/admin/ad-campaigns`),
 axios.get(`${API}/admin/ad-locations`),
 axios.get(`${API}/admin/ad-analytics`),
 axios.get(`${API}/admin/design-system`),
 axios.get(`${API}/admin/design-systems`),
 axios.get(`${API}/admin/theme-presets`),
 axios.get(`${API}/currency-settings`)
 ]);
 // Named unwrap so one broken endpoint no longer kills every tab.
 const pick = (i, fallback) => (results[i].status === 'fulfilled' ? results[i].value.data : fallback);
 const [kbRes, partnersRes, productsRes, videosRes, storeRes, consultingRes, settingsRes, tiersRes, subsRes, modsRes, usersRes, permsRes, mediaRes, campaignsRes, locationsRes, analyticsRes, designRes, designAllRes, presetsRes, currencyRes] = [
   { data: pick(0, []) }, { data: pick(1, []) }, { data: pick(2, []) }, { data: pick(3, []) },
   { data: pick(4, []) }, { data: pick(5, []) }, { data: pick(6, {}) }, { data: pick(7, []) },
   { data: pick(8, []) }, { data: pick(9, []) }, { data: pick(10, []) }, { data: pick(11, { presets: {} }) },
   { data: pick(12, []) }, { data: pick(13, []) }, { data: pick(14, []) }, { data: pick(15, {}) },
   { data: pick(16, null) }, { data: pick(17, []) }, { data: pick(18, []) }, { data: pick(19, { usd_rate: 1 }) },
 ];
 // Surface only the endpoints that actually failed so admins know to fix them.
 const failures = results
   .map((r, i) => (r.status === 'rejected' ? i : -1))
   .filter((i) => i >= 0);
 if (failures.length > 0) {
   console.warn('Admin data endpoints failed:', failures);
 }
 
 setKnowledgeBase(kbRes.data);
 setPartners(partnersRes.data);
 setProducts(productsRes.data);
 setVideos(videosRes.data);
 setStoreItems(storeRes.data);
 setConsultingRequests(consultingRes.data);
 setSettings(settingsRes.data);
 setSubscriptionTiers(tiersRes.data);
 setUserSubscriptions(subsRes.data);
 setModerators(modsRes.data);
 setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []));
 setPermissions(permsRes.data);
 setMediaAssets(mediaRes.data);
 setAdCampaigns(campaignsRes.data);
 setAdLocations(locationsRes.data);
 setAdAnalytics(analyticsRes.data);
 setActiveDesignSystem(designRes.data);
 setDesignSystems(designAllRes.data);
 setThemePresets(presetsRes.data);
 setCurrencySettings(currencyRes.data);
 setUsdRate(currencyRes.data?.usd_rate ?? 1);
 } catch (error) {
 toast.error('Failed to load admin data');
 } finally {
 setLoading(false);
 }
 };

 // ============= KNOWLEDGE BASE =============
 const saveKnowledge = async () => {
 try {
 if (editingKnowledge) {
 await axios.put(`${API}/admin/ai-knowledge/${editingKnowledge.id}`, newKnowledge);
 toast.success('Knowledge updated');
 } else {
 await axios.post(`${API}/admin/ai-knowledge`, newKnowledge);
 toast.success('Knowledge added');
 }
 setShowKnowledgeDialog(false);
 setEditingKnowledge(null);
 setNewKnowledge({ title: '', content: '', category: 'General', enabled: true });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save knowledge');
 }
 };

 const deleteKnowledge = async (id) => {
 if (!window.confirm('Delete this knowledge item?')) return;
 try {
 await axios.delete(`${API}/admin/ai-knowledge/${id}`);
 toast.success('Knowledge deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // ============= PARTNERS =============
 const savePartner = async () => {
 try {
 if (editingPartner) {
 await axios.put(`${API}/partners/${editingPartner.id}`, newPartner);
 toast.success('Partner updated');
 } else {
 await axios.post(`${API}/partners`, newPartner);
 toast.success('Partner added');
 }
 setShowPartnerDialog(false);
 setEditingPartner(null);
 setNewPartner({ name: '', logo: '', description: '', region: '', link: '', featured: false });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save partner');
 }
 };

 const deletePartner = async (id) => {
 if (!window.confirm('Delete this partner?')) return;
 try {
 await axios.delete(`${API}/partners/${id}`);
 toast.success('Partner deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // ============= PRODUCTS =============
 const saveProduct = async () => {
 try {
 if (editingProduct) {
 await axios.put(`${API}/freeze-dry-database/${editingProduct.id}`, newProduct);
 toast.success('Product updated');
 } else {
 await axios.post(`${API}/freeze-dry-database`, newProduct);
 toast.success('Product added');
 }
 setShowProductDialog(false);
 setEditingProduct(null);
 setNewProduct({ product_name: '', category: 'Fruit', water_content: 0, pre_freeze_instructions: '', temp_range: '', pressure_range: '', primary_dry_time: '', secondary_dry_time: '', notes: '' });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save product');
 }
 };

 const deleteProduct = async (id) => {
 if (!window.confirm('Delete this product?')) return;
 try {
 await axios.delete(`${API}/freeze-dry-database/${id}`);
 toast.success('Product deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // ============= VIDEOS =============
 const saveVideo = async () => {
 try {
 if (editingVideo) {
 await axios.put(`${API}/videos/${editingVideo.id}`, newVideo);
 toast.success('Video updated');
 } else {
 await axios.post(`${API}/videos`, newVideo);
 toast.success('Video added');
 }
 setShowVideoDialog(false);
 setEditingVideo(null);
 setNewVideo({ title: '', description: '', url: '', category: 'Beginner', is_pro_only: false });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save video');
 }
 };

 const deleteVideo = async (id) => {
 if (!window.confirm('Delete this video?')) return;
 try {
 await axios.delete(`${API}/videos/${id}`);
 toast.success('Video deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // ============= STORE =============
 const saveStoreItem = async () => {
 try {
 if (editingStore) {
 await axios.put(`${API}/store/${editingStore.id}`, newStoreItem);
 toast.success('Store item updated');
 } else {
 await axios.post(`${API}/store`, newStoreItem);
 toast.success('Store item added');
 }
 setShowStoreDialog(false);
 setEditingStore(null);
 setNewStoreItem({ name: '', description: '', price: 0, image: '', category: 'Accessories', external_url: '' });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save store item');
 }
 };

 const deleteStoreItem = async (id) => {
 if (!window.confirm('Delete this store item?')) return;
 try {
 await axios.delete(`${API}/store/${id}`);
 toast.success('Store item deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete');
 }
 };

 // Currency Settings
 const fetchCurrencySettings = async () => {
 try {
 const response = await axios.get(`${API}/currency-settings`);
 setCurrencySettings(response.data);
 setUsdRate(response.data.usd_rate);
 } catch (error) {
 console.error('Failed to fetch currency settings');
 }
 };

 const updateCurrencyRate = async () => {
 try {
 await axios.put(`${API}/admin/currency-settings?usd_rate=${usdRate}`);
 toast.success('Exchange rate updated');
 fetchCurrencySettings();
 } catch (error) {
 toast.error('Failed to update rate');
 }
 };

 const fetchRateHistory = async () => {
 try {
 const response = await axios.get(`${API}/admin/currency-rate-history`);
 setRateHistory(response.data);
 } catch (error) {
 console.error('Failed to fetch rate history');
 }
 };

 // ============= SETTINGS =============
 const saveSettings = async () => {
 try {
 await axios.put(`${API}/admin/settings`, settings);
 toast.success('Settings saved successfully');
 setSettingsModified(false);
 } catch (error) {
 toast.error('Failed to save settings');
 }
 };

 const updateFeature = (feature, enabled) => {
 setSettings({
 ...settings,
 features: { ...settings.features, [feature]: enabled }
 });
 setSettingsModified(true);
 };

 const updateTheme = (field, value) => {
 setSettings({
 ...settings,
 theme: { ...settings.theme, [field]: value }
 });
 setSettingsModified(true);
 };

 const updateMarketing = (field, value) => {
 setSettings({
 ...settings,
 marketing: { ...settings.marketing, [field]: value }
 });
 setSettingsModified(true);
 };

 // ============= SUBSCRIPTION TIER MANAGEMENT =============
 const saveTier = async () => {
 try {
 if (editingTier) {
 await axios.put(`${API}/admin/subscription-tiers/${editingTier.id}`, newTier);
 toast.success('Tier updated');
 } else {
 await axios.post(`${API}/admin/subscription-tiers`, newTier);
 toast.success('Tier created');
 }
 setShowTierDialog(false);
 setEditingTier(null);
 setNewTier({ name: '', display_name: '', monthly_price: 0, annual_price: 0, description: '', features: [], enabled: true, order: 1, popular: false });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save tier');
 }
 };

 const deleteTier = async (id) => {
 if (!window.confirm('Delete this subscription tier? This will affect existing subscribers!')) return;
 try {
 await axios.delete(`${API}/admin/subscription-tiers/${id}`);
 toast.success('Tier deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete tier');
 }
 };

 const addFeatureToTier = () => {
 if (!newFeature.trim()) return;
 setNewTier({
 ...newTier,
 features: [...newTier.features, newFeature.trim()]
 });
 setNewFeature('');
 };

 const removeFeatureFromTier = (index) => {
 setNewTier({
 ...newTier,
 features: newTier.features.filter((_, i) => i !== index)
 });
 };

 // ============= MODERATOR MANAGEMENT =============
 const appointModerator = async () => {
 if (!newModerator.user_email) {
 toast.error('Please enter user email');
 return;
 }
 try {
 await axios.post(`${API}/admin/moderators`, newModerator);
 toast.success('Moderator appointed');
 setShowModeratorDialog(false);
 setNewModerator({ user_email: '', permissions: [], areas: [], notes: '' });
 fetchAllData();
 } catch (error) {
 toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to appoint moderator');
 }
 };

 const removeModerator = async (modId) => {
 if (!window.confirm('Remove moderator status?')) return;
 try {
 await axios.delete(`${API}/admin/moderators/${modId}`);
 toast.success('Moderator removed');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to remove moderator');
 }
 };

 const updateUserRole = async (userId, newRole) => {
 try {
 await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
 toast.success('User role updated');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to update role');
 }
 };

 const toggleModeratorPermission = (permission) => {
 const perms = newModerator.permissions.includes(permission)
 ? newModerator.permissions.filter(p => p !== permission)
 : [...newModerator.permissions, permission];
 setNewModerator({ ...newModerator, permissions: perms });
 };

 const toggleModeratorArea = (area) => {
 const areas = newModerator.areas.includes(area)
 ? newModerator.areas.filter(a => a !== area)
 : [...newModerator.areas, area];
 setNewModerator({ ...newModerator, areas });
 };

 const applyPreset = (presetName) => {
 const preset = permissions.presets[presetName] || [];
 setNewModerator({ ...newModerator, permissions: preset });
 };

 // Media & Ads Management Functions
 const saveMediaAsset = async () => {
 try {
 if (editingMedia) {
 await axios.put(`${API}/admin/media-assets/${editingMedia.id}`, newMediaAsset);
 toast.success('Media asset updated');
 } else {
 await axios.post(`${API}/admin/media-assets`, newMediaAsset);
 toast.success('Media asset created');
 }
 setShowMediaDialog(false);
 setEditingMedia(null);
 setNewMediaAsset({
 name: '', description: '', media_type: 'image', file_url: '', embed_code: '', 
 embed_url: '', alt_text: '', width: null, height: null, metadata: {}
 });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save media asset');
 }
 };

 const deleteMediaAsset = async (assetId) => {
 if (!confirm('Are you sure you want to delete this media asset?')) return;
 try {
 await axios.delete(`${API}/admin/media-assets/${assetId}`);
 toast.success('Media asset deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete media asset');
 }
 };

 const saveCampaign = async () => {
 try {
 const campaignData = { ...newCampaign };
 
 // Handle date conversions
 if (campaignData.start_date) {
 campaignData.start_date = new Date(campaignData.start_date).toISOString();
 }
 if (campaignData.end_date) {
 campaignData.end_date = new Date(campaignData.end_date).toISOString();
 }
 
 if (editingCampaign) {
 await axios.put(`${API}/admin/ad-campaigns/${editingCampaign.id}`, campaignData);
 toast.success('Campaign updated');
 } else {
 await axios.post(`${API}/admin/ad-campaigns`, campaignData);
 toast.success('Campaign created');
 }
 
 setShowCampaignDialog(false);
 setEditingCampaign(null);
 setNewCampaign({
 name: '', description: '', media_asset_id: '', location: 'dashboard_banner', 
 priority: 'medium', target_user_roles: ['free', 'tier_1', 'tier_2', 'tier_3'], 
 exclude_admin: true, start_date: null, end_date: null, click_url: '', 
 display_order: 0, max_impressions: null, custom_title: '', custom_description: '', custom_cta_text: ''
 });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save campaign');
 }
 };

 const deleteCampaign = async (campaignId) => {
 if (!confirm('Are you sure you want to delete this campaign?')) return;
 try {
 await axios.delete(`${API}/admin/ad-campaigns/${campaignId}`);
 toast.success('Campaign deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete campaign');
 }
 };

 const toggleTargetRole = (role) => {
 const roles = newCampaign.target_user_roles.includes(role)
 ? newCampaign.target_user_roles.filter(r => r !== role)
 : [...newCampaign.target_user_roles, role];
 setNewCampaign({ ...newCampaign, target_user_roles: roles });
 };

 // Design System Management Functions
 const createFromPreset = async (presetName) => {
 try {
 await axios.post(`${API}/admin/design-systems/from-preset/${presetName}`);
 toast.success(`${presetName.replace('_', ' ')} theme created`);
 fetchAllData();
 } catch (error) {
 toast.error('Failed to create theme from preset');
 }
 };

 const activateDesignSystem = async (systemId) => {
 try {
 await axios.post(`${API}/admin/design-systems/${systemId}/activate`);
 toast.success('Design system activated');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to activate design system');
 }
 };

 const deleteDesignSystem = async (systemId) => {
 if (!confirm('Are you sure you want to delete this design system?')) return;
 try {
 await axios.delete(`${API}/admin/design-systems/${systemId}`);
 toast.success('Design system deleted');
 fetchAllData();
 } catch (error) {
 toast.error('Failed to delete design system');
 }
 };

 const saveDesignSystem = async () => {
 try {
 if (editingDesignSystem) {
 await axios.put(`${API}/admin/design-systems/${editingDesignSystem.id}`, newDesignSystem);
 toast.success('Design system updated');
 } else {
 await axios.post(`${API}/admin/design-systems`, newDesignSystem);
 toast.success('Design system created');
 }
 setShowDesignDialog(false);
 setEditingDesignSystem(null);
 setNewDesignSystem({
 name: '', theme_preset: 'custom', colors: {}, typography: {}, spacing: {}, components: {}, branding: {}
 });
 fetchAllData();
 } catch (error) {
 toast.error('Failed to save design system');
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen flex items-center justify-center">
 <p>Loading admin portal...</p>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <div className="bg-background border-b border-border dark:bg-neutral-900 dark:border-neutral-800">
 <div className="container mx-auto px-4 py-4 flex items-center gap-4">
 <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
 <ArrowLeft className="w-5 h-5" />
 </Button>
 <h1 className="text-2xl font-bold dark:text-white">Admin Portal</h1>
 <div className="ml-auto">
 <ThemeToggle />
 </div>
 </div>
 </div>

 <ToolSelector />

 <div className="container mx-auto px-4 py-8">
 <PageTutorial id="dashboard" />

 <AIKnowledgeHealthAlert />

 {/* Admin console — canonical link-out to every admin subpage. Each
     tile opens a full page (not an inline drawer) so back navigation
     is consistent. Hover the ? for a one-line explainer. */}
 <div className="mb-8" data-testid="admin-console">
 <div className="flex items-center gap-2 mb-4">
 <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Admin console</h2>
 <WhatIsThisTip text="Every admin tool in one place. Each tile opens a full page — use the top-left 'Admin portal' pill on any page to return here." />
 </div>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
 {[
 { path: '/admin/users',        label: 'Users',           desc: 'Create, ban, promote', hint: 'Full user list. Click any email to open the User Detail page — status changes, role changes, reset password.' },
 { path: '/admin/invites',      label: 'Invites',         desc: 'Onboard admins/mods', hint: 'Mint a one-time link that auto-provisions the recipient at the target role on first sign-up.' },
 { path: '/admin/mod-room',     label: 'Mod Room',        desc: 'Staff-only threads', hint: 'Private threaded discussion for admins + moderators. Coordinate on tricky reports before acting.' },
 { path: '/admin/moderation',   label: 'Reports queue',   desc: 'Community reports',   hint: 'All open reports on posts/replies. Click Take action to soft-ban, DM, reply publicly, or pin a moderator note in one call.' },
 { path: '/admin/codes',        label: 'Access codes',    desc: 'Tier + content codes', hint: 'HomeLyph/D-Freeze tier codes AND content-scoped codes (events, study sessions, gated courses).' },
 { path: '/admin/certificates', label: 'Certificates',    desc: 'Issued cert numbers', hint: 'Every certificate number issued to a member on course completion. Filter by course or user.' },
 { path: '/admin/lms',          label: 'LMS Admin',       desc: 'Courses + modules',   hint: 'Build courses, modules, and lessons. Toggle Published, Access-code required, and Public/Unlisted/Private visibility here.' },
 { path: '/admin/analytics',    label: 'Analytics',       desc: 'Growth + retention',   hint: 'Aggregate survey profile metrics, engagement, and course-completion rates.' },
 { path: '/admin/workstation',  label: 'Tool Workstation', desc: 'R&D bench for tools', hint: 'Unified catalog of every tool. Toggle Concept → WIP → Beta → Live, gate by tier, open tools inline, and register new ones.' },
 { path: '/admin/content-protection', label: 'Copy-attempt monitor', desc: 'Anti-piracy flags', hint: 'Flags users triggering >20 copy / right-click / print / save attempts in any hour so moderators can act on scrapers.' },
 { path: '/admin/leak-traces', label: 'Leak-trace ledger', desc: 'QR download log', hint: 'Every watermarked PDF/image download is logged with a QR-scannable trace. Search by user, kind, or date range to spot heavy downloaders.' },
 { path: '/admin/refund-requests', label: 'Refund requests', desc: 'Approve / deny', hint: 'User-initiated refund requests within the 14-day window. Approve to auto-cancel the subscription and demote to free.' },
 { path: '/admin/ad-slots', label: 'Ad slots', desc: 'Placements + media kit', hint: 'Every ad placement grouped by size, live previews, per-slot media kit reports, bundle creator, boot-splash video swapper.' },
 ].map((tile) => (
 <button
 key={tile.path}
 type="button"
 onClick={() => navigate(tile.path)}
 className="text-left rounded-xl border border-border p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors relative"
 data-testid={`admin-console-tile-${tile.path.replace(/\//g, '-')}`}
 >
 <div className="flex items-center gap-1.5">
 <span className="font-heading text-sm font-extrabold text-foreground">{tile.label}</span>
 <WhatIsThisTip text={tile.hint} className="ml-auto" />
 </div>
 <span className="text-[11px] text-muted-foreground block mt-0.5">{tile.desc}</span>
 </button>
 ))}
 </div>
 </div>

 {/* Admin Sections - Card Style */}
 <div className="mb-8">
 <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Admin Management</h2>
 <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
 {/* Directory Management */}
 <Card
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30"
 onClick={() => navigate('/admin/directory')}
 data-testid="admin-directory-card"
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <Users className="w-8 h-8" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Directory</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Vendors, consultants & applications</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* AI Gate Hit Report */}
 <Card
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500/40"
 onClick={() => navigate('/admin/ai-gate-hits')}
 data-testid="admin-ai-gate-hits-card"
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-amber-500/10 text-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
 <Lock className="w-8 h-8" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Gate Hit Report</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Feature-gated AI questions · upsell signal</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Study Buddy Matcher */}
 <Card
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-emerald-500/40"
 onClick={() => navigate('/admin/study-buddy')}
 data-testid="admin-study-buddy-card"
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
 <Users2 className="w-8 h-8" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Study Buddy</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Nightly peer matcher · recent pairings</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* AI Moderation */}
 <Card
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-rose-500/40"
 onClick={() => navigate('/admin/moderation')}
 data-testid="admin-moderation-card"
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-rose-500/10 text-rose-600 rounded-2xl flex items-center justify-center shadow-lg">
 <ShieldAlert className="w-8 h-8" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">AI Moderation</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Community flags & misinformation queue</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Learning & Courses */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30"
 onClick={() => navigate('/admin/lms')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <BookOpen className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Learning & Courses</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Manage LMS content</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* AI Knowledge Base */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500"
 onClick={() => setActiveTab('knowledge')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <Brain className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">AI Knowledge</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Control AI assistant</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Content Management */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-green-500"
 onClick={() => setActiveTab('content')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
 <Database className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Content</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Products & videos</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Partners */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-500"
 onClick={() => setActiveTab('partners')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
 <Users className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Partners</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Manage partners</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Subscriptions */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-green-500"
 onClick={() => setActiveTab('subscriptions')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
 <DollarSign className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Subscriptions</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Pricing & tiers</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Moderators */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-red-500"
 onClick={() => setActiveTab('moderators')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-destructive/10 text-destructive rounded-2xl flex items-center justify-center shadow-lg">
 <Shield className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Moderators</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">User permissions</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Media & Ads */}
<Card 
className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-indigo-500"
onClick={() => navigate('/admin/ad-slots')}
data-testid="admin-tile-media-ads"
>
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <Monitor className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Media & Ads</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Manage media</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Design System */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30"
 onClick={() => setActiveTab('design')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <Palette className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Design System</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Colors & branding</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Forms */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-cyan-500"
 onClick={() => setActiveTab('forms')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center shadow-lg">
 <FileText className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Forms</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Consulting requests</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Theme */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-slate-500"
 onClick={() => setActiveTab('theme')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-gray-600 rounded-2xl flex items-center justify-center shadow-lg">
 <Sliders className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Theme</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">App settings</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Features */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30"
 onClick={() => setActiveTab('features')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-lg">
 <Save className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">Features</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Feature flags</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Back to Dashboard */}
 <Card 
 className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-dashed hover:border-slate-400"
 onClick={() => navigate('/dashboard')}
 >
 <CardContent className="pt-6">
 <div className="flex flex-col items-center text-center space-y-3">
 <div className="w-16 h-16 bg-gradient-to-br from-slate-400 to-slate-500 rounded-2xl flex items-center justify-center shadow-lg">
 <ArrowLeft className="w-8 h-8 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-lg text-slate-900 dark:text-white">User Dashboard</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400">Return to main view</p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>

 <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
 <TabsList className="grid w-full grid-cols-11 mb-6">
 <TabsTrigger value="knowledge"><Brain className="w-4 h-4 mr-2" />AI Knowledge</TabsTrigger>
 <TabsTrigger value="standards" data-testid="admin-tab-standards"><BookOpen className="w-4 h-4 mr-2" />Standards</TabsTrigger>
 <TabsTrigger value="content"><Database className="w-4 h-4 mr-2" />Content</TabsTrigger>
 <TabsTrigger value="partners"><Users className="w-4 h-4 mr-2" />Partners</TabsTrigger>
 <TabsTrigger value="subscriptions"><DollarSign className="w-4 h-4 mr-2" />Subscriptions</TabsTrigger>
 <TabsTrigger value="moderators"><Shield className="w-4 h-4 mr-2" />Moderators</TabsTrigger>
 <TabsTrigger value="media" onClick={() => navigate('/admin/ad-slots')} data-testid="admin-tab-media-ads"><Monitor className="w-4 h-4 mr-2" />Media & Ads</TabsTrigger>
 <TabsTrigger value="design"><Palette className="w-4 h-4 mr-2" />Design System</TabsTrigger>
 <TabsTrigger value="forms"><FileText className="w-4 h-4 mr-2" />Forms</TabsTrigger>
 <TabsTrigger value="theme"><Sliders className="w-4 h-4 mr-2" />Theme</TabsTrigger>
 <TabsTrigger value="features"><Save className="w-4 h-4 mr-2" />Features</TabsTrigger>
 </TabsList>

 {/* AI KNOWLEDGE BASE */}
 <TabsContent value="knowledge">
 <div className="space-y-6">
 {/* Documents + Unanswered questions */}
 <KnowledgeBaseManager />

 {/* Manual Text Entries — quick-add + bulk-import + category chips */}
 <TextEntriesManager />
 </div>
 </TabsContent>

 {/* STANDARDS OF EDUCATION */}
 <TabsContent value="standards">
 <EducationStandardsManager />
 </TabsContent>

 {/* CONTENT MANAGEMENT */}
 <TabsContent value="content">
 <div className="space-y-6">
 {/* Products */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <CardTitle>Freeze Dry Products Database</CardTitle>
 <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingProduct(null); setNewProduct({ product_name: '', category: 'Fruit', water_content: 0, pre_freeze_instructions: '', temp_range: '', pressure_range: '', primary_dry_time: '', secondary_dry_time: '', notes: '' }); }}>
 <Plus className="w-4 h-4 mr-2" />Add Product
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editingProduct ? 'Edit' : 'Add'} Product</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>Product Name</Label>
 <Input value={newProduct.product_name} onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})} />
 </div>
 <div>
 <Label>Category</Label>
 <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 {['Fruit', 'Vegetable', 'Candy', 'Meat', 'Seafood', 'Meal', 'Supplement', 'Other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Water Content (%)</Label>
 <Input type="number" value={newProduct.water_content} onChange={(e) => setNewProduct({...newProduct, water_content: parseFloat(e.target.value)})} />
 </div>
 <div>
 <Label>Pre-Freeze Instructions</Label>
 <Textarea value={newProduct.pre_freeze_instructions} onChange={(e) => setNewProduct({...newProduct, pre_freeze_instructions: e.target.value})} />
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Temperature Range</Label>
 <Input value={newProduct.temp_range} onChange={(e) => setNewProduct({...newProduct, temp_range: e.target.value})} placeholder="-40°F to -50°F" />
 </div>
 <div>
 <Label>Pressure Range</Label>
 <Input value={newProduct.pressure_range} onChange={(e) => setNewProduct({...newProduct, pressure_range: e.target.value})} placeholder="200-500 mTorr" />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Primary Dry Time</Label>
 <Input value={newProduct.primary_dry_time} onChange={(e) => setNewProduct({...newProduct, primary_dry_time: e.target.value})} placeholder="8-12 hours" />
 </div>
 <div>
 <Label>Secondary Dry Time</Label>
 <Input value={newProduct.secondary_dry_time} onChange={(e) => setNewProduct({...newProduct, secondary_dry_time: e.target.value})} placeholder="4-6 hours" />
 </div>
 </div>
 <div>
 <Label>Notes</Label>
 <Textarea value={newProduct.notes} onChange={(e) => setNewProduct({...newProduct, notes: e.target.value})} />
 </div>
 <Button onClick={saveProduct} className="w-full">Save Product</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-2">
 {products.map(p => (
 <div key={p.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
 <div>
 <p className="font-medium">{p.product_name}</p>
 <p className="text-sm text-slate-600">{p.category} • {p.water_content}% water</p>
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="ghost" onClick={() => { setEditingProduct(p); setNewProduct(p); setShowProductDialog(true); }}><Edit className="w-4 h-4" /></Button>
 <Button size="sm" variant="ghost" onClick={() => deleteProduct(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Videos */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <CardTitle>Video Library</CardTitle>
 <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingVideo(null); setNewVideo({ title: '', description: '', url: '', category: 'Beginner', is_pro_only: false }); }}>
 <Plus className="w-4 h-4 mr-2" />Add Video
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingVideo ? 'Edit' : 'Add'} Video</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>Title</Label>
 <Input value={newVideo.title} onChange={(e) => setNewVideo({...newVideo, title: e.target.value})} />
 </div>
 <div>
 <Label>Description</Label>
 <Textarea value={newVideo.description} onChange={(e) => setNewVideo({...newVideo, description: e.target.value})} />
 </div>
 <div>
 <Label>Video URL</Label>
 <Input value={newVideo.url} onChange={(e) => setNewVideo({...newVideo, url: e.target.value})} placeholder="https://youtube.com/..." />
 </div>
 <div>
 <Label>Category</Label>
 <Select value={newVideo.category} onValueChange={(v) => setNewVideo({...newVideo, category: v})}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 {['Beginner', 'Advanced', 'Business', 'Technical'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div className="flex items-center gap-2">
 <Switch checked={newVideo.is_pro_only} onCheckedChange={(checked) => setNewVideo({...newVideo, is_pro_only: checked})} />
 <Label>Pro Only</Label>
 </div>
 <Button onClick={saveVideo} className="w-full">Save Video</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-2">
 {videos.map(v => (
 <div key={v.id} className="flex justify-between items-center p-3 bg-slate-50 rounded">
 <div>
 <p className="font-medium">{v.title}</p>
 <p className="text-sm text-slate-600">{v.category} {v.is_pro_only && '• Pro Only'}</p>
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="ghost" onClick={() => { setEditingVideo(v); setNewVideo(v); setShowVideoDialog(true); }}><Edit className="w-4 h-4" /></Button>
 <Button size="sm" variant="ghost" onClick={() => deleteVideo(v.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Store Items */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <CardTitle>Store Items</CardTitle>
 <Dialog open={showStoreDialog} onOpenChange={setShowStoreDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingStore(null); setNewStoreItem({ name: '', description: '', price: 0, image: '', category: 'Accessories', external_url: '' }); }}>
 <Plus className="w-4 h-4 mr-2" />Add Item
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingStore ? 'Edit' : 'Add'} Store Item</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>Name</Label>
 <Input value={newStoreItem.name} onChange={(e) => setNewStoreItem({...newStoreItem, name: e.target.value})} />
 </div>
 <div>
 <Label>Description</Label>
 <Textarea value={newStoreItem.description} onChange={(e) => setNewStoreItem({...newStoreItem, description: e.target.value})} />
 </div>
 <div>
 <Label>MSRP / Regular Price (CAD)</Label>
 <Input 
 type="number" 
 step="0.01" 
 value={newStoreItem.msrp || ''} 
 onChange={(e) => setNewStoreItem({...newStoreItem, msrp: parseFloat(e.target.value) || 0})} 
 placeholder="Original price (optional)"
 />
 {newStoreItem.msrp > 0 && usdRate && (
 <p className="text-xs text-slate-500 mt-1">
 🇺🇸 USD: ${(newStoreItem.msrp * usdRate).toFixed(2)}
 </p>
 )}
 </div>
 <div>
 <Label>Current Price (CAD)</Label>
 <Input type="number" step="0.01" value={newStoreItem.price} onChange={(e) => setNewStoreItem({...newStoreItem, price: parseFloat(e.target.value)})} />
 {newStoreItem.price > 0 && usdRate && (
 <p className="text-xs text-slate-500 mt-1">
 🇺🇸 USD: ${(newStoreItem.price * usdRate).toFixed(2)}
 </p>
 )}
 {newStoreItem.msrp > 0 && newStoreItem.price > 0 && newStoreItem.msrp > newStoreItem.price && (
 <p className="text-xs text-green-600 font-semibold mt-1">
 💰 Save ${(newStoreItem.msrp - newStoreItem.price).toFixed(2)} ({Math.round(((newStoreItem.msrp - newStoreItem.price) / newStoreItem.msrp) * 100)}% off)
 </p>
 )}
 </div>
 <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
 <input
 type="checkbox"
 id="on_sale"
 checked={newStoreItem.on_sale}
 onChange={(e) => setNewStoreItem({...newStoreItem, on_sale: e.target.checked})}
 className="w-4 h-4"
 />
 <Label htmlFor="on_sale" className="cursor-pointer">
 🏷️ Mark as "On Sale" (shows sale banner)
 </Label>
 </div>
 <div>
 <Label>Category</Label>
 <select 
 value={newStoreItem.category} 
 onChange={(e) => setNewStoreItem({...newStoreItem, category: e.target.value})}
 className="w-full p-2 border rounded-lg bg-white dark:bg-slate-800"
 >
 <option value="Accessories">Accessories</option>
 <option value="Equipment">Equipment</option>
 <option value="Supports">Supports</option>
 </select>
 </div>
 <div>
 <Label>Machine Name</Label>
 <select 
 value={newStoreItem.machine_name || ''} 
 onChange={(e) => setNewStoreItem({...newStoreItem, machine_name: e.target.value})}
 className="w-full p-2 border rounded-lg bg-white dark:bg-slate-800"
 >
 <option value="">Select Machine Category (Optional)</option>
 <option value="Small Freeze Dryer">Small Freeze Dryer</option>
 <option value="Medium Freeze Dryer">Medium Freeze Dryer</option>
 <option value="Large Freeze Dryer">Large Freeze Dryer</option>
 <option value="Commercial Freeze Dryer">Commercial Freeze Dryer</option>
 <option value="Industrial Freeze Dryer">Industrial Freeze Dryer</option>
 <option value="Other">Other / Custom</option>
 </select>
 </div>
 <div>
 <Label>Product Image URL</Label>
 <Input 
 value={newStoreItem.image} 
 onChange={(e) => setNewStoreItem({...newStoreItem, image: e.target.value})} 
 placeholder="https://example.com/image.jpg" 
 />
 {newStoreItem.image && (
 <div className="mt-2">
 <img 
 src={newStoreItem.image} 
 alt="Product preview" 
 className="w-32 h-32 object-cover rounded border"
 onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL'}
 />
 </div>
 )}
 </div>
 <div>
 <Label>Company Logo URL (Optional)</Label>
 <Input 
 value={newStoreItem.company_logo || ''} 
 onChange={(e) => setNewStoreItem({...newStoreItem, company_logo: e.target.value})} 
 placeholder="https://example.com/logo.png" 
 />
 {newStoreItem.company_logo && (
 <div className="mt-2">
 <img 
 src={newStoreItem.company_logo} 
 alt="Company logo preview" 
 className="w-24 h-12 object-contain rounded border bg-white p-1"
 onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Invalid+Logo'}
 />
 </div>
 )}
 </div>
 <div>
 <Label>External URL (Store Link)</Label>
 <Input value={newStoreItem.external_url} onChange={(e) => setNewStoreItem({...newStoreItem, external_url: e.target.value})} placeholder="https://..." />
 </div>
 <Button onClick={saveStoreItem} className="w-full">Save Item</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {/* Currency Settings */}
 <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 💱 Currency Exchange Rate
 </h4>
 <div className="space-y-3">
 <div className="flex items-center gap-4">
 <div className="flex-1">
 <Label>USD Exchange Rate (1 CAD = ? USD)</Label>
 <Input 
 type="number" 
 step="0.01" 
 value={usdRate} 
 onChange={(e) => setUsdRate(parseFloat(e.target.value))}
 className="mt-1"
 />
 <p className="text-xs text-slate-600 dark:text-neutral-400 mt-1">
 Example: 0.74 means $1 CAD = $0.74 USD
 </p>
 </div>
 <div className="pt-6">
 <Button onClick={updateCurrencyRate}>
 Update Rate
 </Button>
 </div>
 </div>
 
 {currencySettings && (
 <div className="text-sm text-slate-600 dark:text-neutral-400">
 <p>Last updated: {new Date(currencySettings.last_updated).toLocaleString()}</p>
 {currencySettings.updated_by && <p>By: {currencySettings.updated_by}</p>}
 </div>
 )}
 
 <Button 
 variant="outline" 
 size="sm" 
 onClick={() => {
 setShowRateHistory(!showRateHistory);
 if (!showRateHistory) fetchRateHistory();
 }}
 >
 {showRateHistory ? 'Hide' : 'Show'} Rate History
 </Button>
 
 {showRateHistory && (
 <div className="mt-3 max-h-48 overflow-y-auto border rounded p-2">
 {rateHistory.length > 0 ? (
 <div className="space-y-2">
 {rateHistory.map((entry) => (
 <div key={entry.id} className="text-xs p-2 bg-white dark:bg-slate-800 rounded">
 <p><strong>{new Date(entry.updated_at).toLocaleString()}</strong></p>
 <p>{entry.old_rate} → {entry.new_rate} (by {entry.updated_by})</p>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-xs text-slate-500">No history available</p>
 )}
 </div>
 )}
 </div>
 </div>

 <div className="space-y-2">
 {storeItems.map(item => (
 <div key={item.id} className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded gap-3">
 {item.image && (
 <img 
 src={item.image} 
 alt={item.name} 
 className="w-16 h-16 object-cover rounded border"
 onError={(e) => e.target.style.display = 'none'}
 />
 )}
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <p className="font-medium">{item.name}</p>
 {item.on_sale && (
 <span className="px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs font-bold rounded">
 SALE
 </span>
 )}
 </div>
 <p className="text-sm text-slate-600 dark:text-neutral-400">
 {item.msrp && item.msrp > item.price ? (
 <>
 <span className="line-through text-slate-400">${item.msrp}</span>
 {' '}
 <span className="text-green-600 font-semibold">${item.price}</span>
 </>
 ) : (
 `$${item.price}`
 )}
 {' • '}{item.category}
 {item.machine_name && ` • ${item.machine_name}`}
 </p>
 {item.image && <p className="text-xs text-slate-500 dark:text-neutral-500 mt-1">📷 Image attached</p>}
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="ghost" onClick={() => { setEditingStore(item); setNewStoreItem(item); setShowStoreDialog(true); }}><Edit className="w-4 h-4" /></Button>
 <Button size="sm" variant="ghost" onClick={() => deleteStoreItem(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>
 </TabsContent>

 {/* PARTNERS */}
 <TabsContent value="partners">
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Partners & Associations</CardTitle>
 <CardDescription>Manage industry partners and associations</CardDescription>
 </div>
 <Dialog open={showPartnerDialog} onOpenChange={setShowPartnerDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingPartner(null); setNewPartner({ name: '', logo: '', description: '', region: '', link: '', featured: false }); }}>
 <Plus className="w-4 h-4 mr-2" />Add Partner
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{editingPartner ? 'Edit' : 'Add'} Partner</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>Name</Label>
 <Input value={newPartner.name} onChange={(e) => setNewPartner({...newPartner, name: e.target.value})} />
 </div>
 <div>
 <Label>Logo URL (optional)</Label>
 <Input value={newPartner.logo} onChange={(e) => setNewPartner({...newPartner, logo: e.target.value})} placeholder="https://..." />
 {newPartner.logo && (
 <div className="mt-2 p-2 bg-white dark:bg-slate-800 border rounded">
 <img 
 src={newPartner.logo} 
 alt="Partner logo preview" 
 className="h-16 object-contain"
 onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Invalid+Logo'}
 />
 </div>
 )}
 </div>
 <div>
 <Label>Description</Label>
 <Textarea value={newPartner.description} onChange={(e) => setNewPartner({...newPartner, description: e.target.value})} />
 </div>
 <div>
 <Label>Region</Label>
 <Input value={newPartner.region} onChange={(e) => setNewPartner({...newPartner, region: e.target.value})} placeholder="e.g., North America" />
 </div>
 <div>
 <Label>Website</Label>
 <Input value={newPartner.link} onChange={(e) => setNewPartner({...newPartner, link: e.target.value})} placeholder="https://..." />
 </div>
 <div className="flex items-center gap-2">
 <Switch checked={newPartner.featured} onCheckedChange={(checked) => setNewPartner({...newPartner, featured: checked})} />
 <Label>Featured Partner</Label>
 </div>
 <Button onClick={savePartner} className="w-full">Save Partner</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {partners.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No partners yet</p>
 ) : (
 <div className="space-y-3">
 {partners.map(partner => (
 <Card key={partner.id} className="p-4">
 <div className="flex justify-between items-start gap-4">
 {partner.logo && (
 <div className="w-24 h-16 flex items-center justify-center bg-white dark:bg-slate-800 border rounded p-2">
 <img 
 src={partner.logo} 
 alt={partner.name}
 className="max-h-full max-w-full object-contain"
 onError={(e) => e.target.style.display = 'none'}
 />
 </div>
 )}
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <h3 className="font-semibold">{partner.name}</h3>
 {partner.featured && <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded">Featured</span>}
 </div>
 {partner.region && <p className="text-sm text-slate-600 dark:text-neutral-400 mb-1">📍 {partner.region}</p>}
 {partner.description && <p className="text-sm text-slate-600 dark:text-neutral-400">{partner.description}</p>}
 {partner.link && (
 <a href={partner.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary dark:text-blue-400 hover:underline mt-1 inline-block">
 Visit Website →
 </a>
 )}
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="ghost" onClick={() => { setEditingPartner(partner); setNewPartner(partner); setShowPartnerDialog(true); }}>
 <Edit className="w-4 h-4" />
 </Button>
 <Button size="sm" variant="ghost" onClick={() => deletePartner(partner.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 </div>
 </Card>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 {/* FORMS */}

 {/* SUBSCRIPTIONS */}
 <TabsContent value="subscriptions">
 <div className="space-y-6">
 {/* Subscription Tiers Management */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Subscription Tiers & Pricing</CardTitle>
 <CardDescription>Manage membership plans, pricing, and features</CardDescription>
 </div>
 <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { 
 setEditingTier(null); 
 setNewTier({ name: '', display_name: '', monthly_price: 0, annual_price: 0, description: '', features: [], enabled: true, order: subscriptionTiers.length + 1, popular: false }); 
 }}>
 <Plus className="w-4 h-4 mr-2" />Add Tier
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editingTier ? 'Edit' : 'Create'} Subscription Tier</DialogTitle>
 <DialogDescription>Set pricing and features for this membership tier</DialogDescription>
 </DialogHeader>
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Tier Name (Internal)</Label>
 <Input value={newTier.name} onChange={(e) => setNewTier({...newTier, name: e.target.value})} placeholder="Tier_1" />
 <p className="text-xs text-slate-500 mt-1">Used internally, no spaces</p>
 </div>
 <div>
 <Label>Display Name</Label>
 <Input value={newTier.display_name} onChange={(e) => setNewTier({...newTier, display_name: e.target.value})} placeholder="Professional" />
 <p className="text-xs text-slate-500 mt-1">Shown to customers</p>
 </div>
 </div>

 <div>
 <Label>Description</Label>
 <Textarea value={newTier.description} onChange={(e) => setNewTier({...newTier, description: e.target.value})} placeholder="Core business tools and AI features" rows={2} />
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Monthly Price ($)</Label>
 <Input type="number" step="0.01" value={newTier.monthly_price} onChange={(e) => setNewTier({...newTier, monthly_price: parseFloat(e.target.value)})} />
 </div>
 <div>
 <Label>Annual Price ($)</Label>
 <Input type="number" step="0.01" value={newTier.annual_price} onChange={(e) => setNewTier({...newTier, annual_price: parseFloat(e.target.value)})} />
 <p className="text-xs text-slate-500 mt-1">
 {newTier.monthly_price > 0 && newTier.annual_price > 0 && (
 <span className="text-green-600">
 {((1 - (newTier.annual_price / (newTier.monthly_price * 12))) * 100).toFixed(0)}% savings
 </span>
 )}
 </p>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Display Order</Label>
 <Input type="number" value={newTier.order} onChange={(e) => setNewTier({...newTier, order: parseInt(e.target.value)})} />
 </div>
 <div className="space-y-2 pt-6">
 <div className="flex items-center gap-2">
 <Switch checked={newTier.enabled} onCheckedChange={(checked) => setNewTier({...newTier, enabled: checked})} />
 <Label>Tier Enabled</Label>
 </div>
 <div className="flex items-center gap-2">
 <Switch checked={newTier.popular} onCheckedChange={(checked) => setNewTier({...newTier, popular: checked})} />
 <Label>Mark as Popular</Label>
 </div>
 </div>
 </div>

 <div className="border-t pt-4">
 <Label className="mb-2 block">Features Included</Label>
 <div className="flex gap-2 mb-3">
 <Input 
 value={newFeature} 
 onChange={(e) => setNewFeature(e.target.value)}
 placeholder="Add a feature..."
 onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeatureToTier())}
 />
 <Button type="button" onClick={addFeatureToTier}>
 <Plus className="w-4 h-4" />
 </Button>
 </div>
 {newTier.features.length > 0 ? (
 <div className="space-y-2 max-h-48 overflow-y-auto">
 {newTier.features.map((feature, index) => (
 <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
 <span className="text-sm">{feature}</span>
 <Button size="sm" variant="ghost" onClick={() => removeFeatureFromTier(index)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">No features added yet</p>
 )}
 </div>

 <Button onClick={saveTier} className="w-full">
 {editingTier ? 'Update' : 'Create'} Tier
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {subscriptionTiers.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No subscription tiers yet</p>
 ) : (
 <div className="space-y-4">
 {subscriptionTiers.sort((a, b) => a.order - b.order).map(tier => (
 <Card key={tier.id} className="p-4">
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <Crown className="w-5 h-5 text-yellow-500" />
 <h3 className="font-bold text-lg">{tier.display_name}</h3>
 {tier.popular && <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">Popular</span>}
 {!tier.enabled && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Disabled</span>}
 </div>
 <p className="text-sm text-slate-600 mb-3">{tier.description}</p>
 
 <div className="grid grid-cols-3 gap-4 mb-3">
 <div>
 <p className="text-xs text-slate-500">Monthly</p>
 <p className="text-2xl font-bold text-green-600">${tier.monthly_price}</p>
 </div>
 <div>
 <p className="text-xs text-slate-500">Annual</p>
 <p className="text-2xl font-bold text-green-600">${tier.annual_price}</p>
 </div>
 <div>
 <p className="text-xs text-slate-500">Order</p>
 <p className="text-2xl font-bold">#{tier.order}</p>
 </div>
 </div>

 <div>
 <p className="text-sm font-medium mb-2">Features ({tier.features.length}):</p>
 <div className="flex flex-wrap gap-1">
 {tier.features.slice(0, 3).map((feature, i) => (
 <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
 {feature}
 </span>
 ))}
 {tier.features.length > 3 && (
 <span className="text-xs text-slate-500">+{tier.features.length - 3} more</span>
 )}
 </div>
 </div>
 </div>

 <div className="flex gap-2 ml-4">
 <Button size="sm" variant="ghost" onClick={() => { 
 setEditingTier(tier); 
 setNewTier(tier); 
 setShowTierDialog(true); 
 }}>
 <Edit className="w-4 h-4" />
 </Button>
 <Button size="sm" variant="ghost" onClick={() => deleteTier(tier.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 </div>
 </Card>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Active Subscriptions */}
 <Card>
 <CardHeader>
 <CardTitle>User Subscriptions</CardTitle>
 <CardDescription>View and manage active user subscriptions</CardDescription>
 </CardHeader>
 <CardContent>
 {userSubscriptions.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No active subscriptions yet</p>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b">
 <th className="text-left py-3 px-4">User</th>
 <th className="text-left py-3 px-4">Tier</th>
 <th className="text-left py-3 px-4">Billing</th>
 <th className="text-left py-3 px-4">Status</th>
 <th className="text-left py-3 px-4">Started</th>
 <th className="text-left py-3 px-4">Expires</th>
 </tr>
 </thead>
 <tbody>
 {userSubscriptions.map(sub => (
 <tr key={sub.id} className="border-b hover:bg-slate-50">
 <td className="py-3 px-4">{sub.user_id}</td>
 <td className="py-3 px-4">
 <span className="inline-block bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded">
 {sub.tier_name}
 </span>
 </td>
 <td className="py-3 px-4 capitalize">{sub.billing_cycle}</td>
 <td className="py-3 px-4">
 <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${
 sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
 }`}>
 {sub.status}
 </span>
 </td>
 <td className="py-3 px-4">{new Date(sub.started_at).toLocaleDateString()}</td>
 <td className="py-3 px-4">{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'N/A'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </TabsContent>

 {/* MODERATORS & PERMISSIONS */}
 <TabsContent value="moderators">
 <div className="space-y-6">
 {/* Moderator Management */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Moderators & Permissions</CardTitle>
 <CardDescription>Manage community moderators and their permissions</CardDescription>
 </div>
 <Dialog open={showModeratorDialog} onOpenChange={setShowModeratorDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => setNewModerator({ user_email: '', permissions: [], areas: [], notes: '' })}>
 <Plus className="w-4 h-4 mr-2" />Appoint Moderator
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Appoint New Moderator</DialogTitle>
 <DialogDescription>Select user and assign permissions</DialogDescription>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label>User Email</Label>
 <Select value={newModerator.user_email} onValueChange={(v) => setNewModerator({...newModerator, user_email: v})}>
 <SelectTrigger>
 <SelectValue placeholder="Select user..." />
 </SelectTrigger>
 <SelectContent>
 {allUsers.filter(u => u.role === 'free' || u.role.startsWith('tier_')).map(user => (
 <SelectItem key={user.id} value={user.email}>{user.email}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="border-t pt-4">
 <Label className="mb-2 block">Permission Presets</Label>
 <div className="flex flex-wrap gap-2 mb-4">
 <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('forum_moderator')}>
 Forum Moderator
 </Button>
 <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('content_moderator')}>
 Content Moderator
 </Button>
 <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('community_manager')}>
 Community Manager
 </Button>
 </div>

 <Label className="mb-2 block">Individual Permissions</Label>
 <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
 {Object.entries(permissions.permissions || {}).map(([key, description]) => (
 <div key={key} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
 <Switch 
 checked={newModerator.permissions.includes(key)}
 onCheckedChange={() => toggleModeratorPermission(key)}
 />
 <div>
 <p className="text-sm font-medium">{key.split('.')[1]}</p>
 <p className="text-xs text-slate-500">{description}</p>
 </div>
 </div>
 ))}
 </div>
 </div>

 <div className="border-t pt-4">
 <Label className="mb-2 block">Moderation Areas</Label>
 <div className="flex flex-wrap gap-2">
 {['forum', 'recipes', 'videos', 'store', 'content'].map(area => (
 <Button
 key={area}
 type="button"
 size="sm"
 variant={newModerator.areas.includes(area) ? "default" : "outline"}
 onClick={() => toggleModeratorArea(area)}
 >
 {area}
 </Button>
 ))}
 </div>
 </div>

 <div>
 <Label>Notes (Optional)</Label>
 <Textarea 
 value={newModerator.notes} 
 onChange={(e) => setNewModerator({...newModerator, notes: e.target.value})}
 placeholder="Any notes about this moderator's responsibilities..."
 rows={3}
 />
 </div>

 <Button onClick={appointModerator} className="w-full">
 Appoint Moderator
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {moderators.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No moderators appointed yet</p>
 ) : (
 <div className="space-y-4">
 {moderators.map(mod => (
 <Card key={mod.id} className="p-4">
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <Shield className="w-5 h-5 text-primary" />
 <h3 className="font-bold">{mod.user_email}</h3>
 {!mod.active && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Inactive</span>}
 </div>
 
 <div className="grid grid-cols-2 gap-4 mb-3">
 <div>
 <p className="text-xs text-slate-500 mb-1">Permissions ({mod.permissions.length})</p>
 <div className="flex flex-wrap gap-1">
 {mod.permissions.slice(0, 3).map((perm, i) => (
 <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
 {perm.split('.')[1]}
 </span>
 ))}
 {mod.permissions.length > 3 && (
 <span className="text-xs text-slate-500">+{mod.permissions.length - 3}</span>
 )}
 </div>
 </div>
 <div>
 <p className="text-xs text-slate-500 mb-1">Areas ({mod.areas.length})</p>
 <div className="flex flex-wrap gap-1">
 {mod.areas.map((area, i) => (
 <span key={i} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded capitalize">
 {area}
 </span>
 ))}
 </div>
 </div>
 </div>

 {mod.notes && (
 <p className="text-sm text-slate-600 mt-2">{mod.notes}</p>
 )}

 <p className="text-xs text-slate-400 mt-2">
 Appointed {new Date(mod.appointed_at).toLocaleDateString()}
 </p>
 </div>

 <div className="flex gap-2 ml-4">
 <Button size="sm" variant="ghost" onClick={() => removeModerator(mod.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 </div>
 </Card>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* User Roles Management */}
 <Card>
 <CardHeader>
 <CardTitle>User Role Management</CardTitle>
 <CardDescription>View and update user access levels</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b">
 <th className="text-left py-3 px-4">Email</th>
 <th className="text-left py-3 px-4">Current Role</th>
 <th className="text-left py-3 px-4">Registered</th>
 <th className="text-left py-3 px-4">Actions</th>
 </tr>
 </thead>
 <tbody>
 {allUsers.map(user => (
 <tr key={user.id} className="border-b hover:bg-slate-50">
 <td className="py-3 px-4">{user.email}</td>
 <td className="py-3 px-4">
 <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${
 user.role === 'admin' ? 'bg-primary/5 text-primary' :
 user.role === 'moderator' ? 'bg-blue-100 text-blue-800' :
 user.role.startsWith('tier_') ? 'bg-green-100 text-green-800' :
 'bg-slate-100 text-slate-800'
 }`}>
 {user.role === 'admin' ? 'Admin' :
 user.role === 'moderator' ? 'Moderator' :
 user.role.startsWith('tier_') ? user.role.replace('_', ' ') :
 'Free User'}
 </span>
 </td>
 <td className="py-3 px-4">{new Date(user.created_at).toLocaleDateString()}</td>
 <td className="py-3 px-4">
 {user.role !== 'admin' && (
 <Select onValueChange={(newRole) => updateUserRole(user.id, newRole)}>
 <SelectTrigger className="w-40">
 <SelectValue placeholder="Change role..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="free">Free User</SelectItem>
 <SelectItem value="moderator">Moderator</SelectItem>
 <SelectItem value="tier_1">Tier 1</SelectItem>
 <SelectItem value="tier_2">Tier 2</SelectItem>
 <SelectItem value="tier_3">Tier 3</SelectItem>
 </SelectContent>
 </Select>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>

 {/* Permission Reference */}
 <Card>
 <CardHeader>
 <CardTitle>Available Permissions Reference</CardTitle>
 <CardDescription>Complete list of all moderator permissions</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-2 gap-4">
 {Object.entries(permissions.permissions || {}).map(([key, description]) => (
 <div key={key} className="flex items-start gap-2 p-3 bg-slate-50 rounded">
 <Lock className="w-4 h-4 text-slate-400 mt-1" />
 <div>
 <p className="font-medium text-sm">{key}</p>
 <p className="text-xs text-slate-600">{description}</p>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>
 </TabsContent>
 <TabsContent value="forms">
 <Card>
 <CardHeader>
 <CardTitle>Consulting Requests</CardTitle>
 <CardDescription>View and manage user consultation requests</CardDescription>
 </CardHeader>
 <CardContent>
 {consultingRequests.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No consulting requests yet</p>
 ) : (
 <div className="space-y-3">
 {consultingRequests.map(req => (
 <Card key={req.id} className="p-4">
 <div className="flex justify-between items-start mb-2">
 <div>
 <p className="font-medium">{req.user_email}</p>
 <p className="text-sm text-slate-500">{new Date(req.created_at).toLocaleString()}</p>
 </div>
 <span className={`text-xs px-2 py-1 rounded ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
 {req.status}
 </span>
 </div>
 <div className="space-y-2 text-sm">
 <div>
 <span className="font-medium">Topic:</span> {req.topic}
 </div>
 <div>
 <span className="font-medium">Preferred Times:</span> {req.preferred_times}
 </div>
 <div>
 <span className="font-medium">Contact:</span> {req.contact_info}
 </div>
 </div>
 </Card>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 {/* MEDIA & ADS */}
 <TabsContent value="media">
 <div className="space-y-6">
 {/* Prominent link to the new full Ad Slot Manager — this is the
     screen every ad-related task should start from. */}
 <Card className="border-2 border-primary/50 bg-primary/5" data-testid="media-tab-manager-cta">
 <CardContent className="pt-6 pb-6">
 <div className="flex items-center gap-4 flex-wrap">
 <div className="w-12 h-12 bg-primary text-primary-foreground rounded-2xl flex items-center justify-center flex-shrink-0">
 <Monitor className="w-6 h-6" />
 </div>
 <div className="flex-1 min-w-[220px]">
 <h3 className="font-heading text-lg font-extrabold text-foreground">Open the Ad Slot Manager</h3>
 <p className="text-sm text-muted-foreground">
 The full ops screen: 43 placements grouped by size, live previews, media kits for advertisers, one-click bundles, boot-splash video swapper.
 </p>
 </div>
 <Button
 onClick={() => navigate('/admin/ad-slots')}
 className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
 data-testid="media-tab-open-manager"
 >
 Open Ad Slot Manager →
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Analytics Overview */}
 <Card>
 <CardHeader>
 <CardTitle>Advertising Overview</CardTitle>
 <CardDescription>Campaign performance and statistics</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-4 gap-4">
 <div className="text-center p-4 bg-blue-50 rounded">
 <div className="text-2xl font-bold text-primary">{adAnalytics.total_campaigns || 0}</div>
 <div className="text-sm text-slate-600">Total Campaigns</div>
 </div>
 <div className="text-center p-4 bg-green-50 rounded">
 <div className="text-2xl font-bold text-green-600">{adAnalytics.active_campaigns || 0}</div>
 <div className="text-sm text-slate-600">Active Campaigns</div>
 </div>
 <div className="text-center p-4 bg-primary/5 rounded">
 <div className="text-2xl font-bold text-primary">{adAnalytics.total_impressions?.toLocaleString() || 0}</div>
 <div className="text-sm text-slate-600">Total Impressions</div>
 </div>
 <div className="text-center p-4 bg-orange-50 rounded">
 <div className="text-2xl font-bold text-orange-600">{adAnalytics.total_clicks?.toLocaleString() || 0}</div>
 <div className="text-sm text-slate-600">Total Clicks</div>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Premium Ad-Free Feature */}
 <Card>
 <CardHeader>
 <CardTitle>Premium Ad-Free Experience</CardTitle>
 <CardDescription>Automatic ad hiding for paid members</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
 <div className="flex items-center gap-3 mb-4">
 <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center">
 <Crown className="w-4 h-4" />
 </div>
 <div>
 <h3 className="font-semibold text-green-800">Ad-Free for Paid Members</h3>
 <p className="text-sm text-green-700">Users with active paid subscriptions automatically see no ads</p>
 </div>
 </div>
 
 <div className="grid md:grid-cols-2 gap-4 text-sm">
 <div>
 <h4 className="font-medium text-slate-900 mb-2">✅ Benefits</h4>
 <ul className="space-y-1 text-slate-600">
 <li>• Premium user experience</li>
 <li>• Automatic based on subscription status</li>
 <li>• Applies across all pages and locations</li>
 <li>• No manual configuration needed</li>
 </ul>
 </div>
 <div>
 <h4 className="font-medium text-slate-900 mb-2">📊 Who Sees Ads</h4>
 <ul className="space-y-1 text-slate-600">
 <li>• Free/unregistered users: <strong>All ads</strong></li>
 <li>• Expired subscriptions: <strong>All ads</strong></li>
 <li>• Active paid members: <strong>No ads</strong></li>
 <li>• Admin users: <strong>Configurable</strong></li>
 </ul>
 </div>
 </div>
 
 <div className="mt-4 p-3 bg-white/80 rounded border">
 <p className="text-xs text-slate-600">
 <strong>System Status:</strong> Premium ad-free experience is automatically active. 
 Ads are hidden for users with active subscriptions (tier_1, tier_2, tier_3) that haven&apos;t expired.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Media Assets Management */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Media Assets</CardTitle>
 <CardDescription>Manage images, videos, and media content for campaigns</CardDescription>
 </div>
 <Dialog open={showMediaDialog} onOpenChange={setShowMediaDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingMedia(null); setNewMediaAsset({
 name: '', description: '', media_type: 'image', file_url: '', embed_code: '', 
 embed_url: '', alt_text: '', width: null, height: null, metadata: {}
 }); }}>
 <Plus className="w-4 h-4 mr-2" />Add Media Asset
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>{editingMedia ? 'Edit' : 'Add'} Media Asset</DialogTitle>
 <DialogDescription>Upload or link media content for advertising campaigns</DialogDescription>
 </DialogHeader>
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Asset Name</Label>
 <Input value={newMediaAsset.name} onChange={(e) => setNewMediaAsset({...newMediaAsset, name: e.target.value})} placeholder="Hero video, Banner image..." />
 </div>
 <div>
 <Label>Media Type</Label>
 <Select value={newMediaAsset.media_type} onValueChange={(value) => setNewMediaAsset({...newMediaAsset, media_type: value})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="image">Static Image</SelectItem>
 <SelectItem value="video">Video File</SelectItem>
 <SelectItem value="video_embed">Video Embed (YouTube/Vimeo)</SelectItem>
 <SelectItem value="gif">Animated GIF</SelectItem>
 <SelectItem value="html_banner">HTML Banner</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 
 <div>
 <Label>Description</Label>
 <Textarea value={newMediaAsset.description} onChange={(e) => setNewMediaAsset({...newMediaAsset, description: e.target.value})} />
 </div>
 
 {newMediaAsset.media_type !== 'html_banner' && (
 <div>
 <Label>File URL</Label>
 <Input value={newMediaAsset.file_url} onChange={(e) => setNewMediaAsset({...newMediaAsset, file_url: e.target.value})} placeholder="https://..." />
 </div>
 )}
 
 {newMediaAsset.media_type === 'video_embed' && (
 <div>
 <Label>Embed URL</Label>
 <Input value={newMediaAsset.embed_url} onChange={(e) => setNewMediaAsset({...newMediaAsset, embed_url: e.target.value})} placeholder="YouTube or Vimeo URL..." />
 </div>
 )}
 
 {newMediaAsset.media_type === 'html_banner' && (
 <div>
 <Label>HTML/Embed Code</Label>
 <Textarea value={newMediaAsset.embed_code} onChange={(e) => setNewMediaAsset({...newMediaAsset, embed_code: e.target.value})} rows={6} placeholder="<iframe src=... or custom HTML" />
 </div>
 )}
 
 <div className="grid grid-cols-3 gap-4">
 <div>
 <Label>Alt Text</Label>
 <Input value={newMediaAsset.alt_text} onChange={(e) => setNewMediaAsset({...newMediaAsset, alt_text: e.target.value})} placeholder="Descriptive text..." />
 </div>
 <div>
 <Label>Width (px)</Label>
 <Input type="number" value={newMediaAsset.width || ''} onChange={(e) => setNewMediaAsset({...newMediaAsset, width: e.target.value ? parseInt(e.target.value) : null})} />
 </div>
 <div>
 <Label>Height (px)</Label>
 <Input type="number" value={newMediaAsset.height || ''} onChange={(e) => setNewMediaAsset({...newMediaAsset, height: e.target.value ? parseInt(e.target.value) : null})} />
 </div>
 </div>
 
 <Button onClick={saveMediaAsset} className="w-full">Save Media Asset</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {mediaAssets.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No media assets yet. Add some to create ad campaigns.</p>
 ) : (
 <div className="space-y-3">
 {mediaAssets.map(asset => (
 <div key={asset.id} className="flex justify-between items-center p-4 border rounded">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
 {asset.media_type === 'image' && <Image className="w-6 h-6" />}
 {asset.media_type === 'video' && <Video className="w-6 h-6" />}
 {asset.media_type === 'video_embed' && <Monitor className="w-6 h-6" />}
 </div>
 <div>
 <p className="font-medium">{asset.name}</p>
 <p className="text-sm text-slate-600">{asset.media_type} • {asset.active ? 'Active' : 'Inactive'}</p>
 <p className="text-xs text-slate-500">{asset.description}</p>
 </div>
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="ghost" onClick={() => { setEditingMedia(asset); setNewMediaAsset(asset); setShowMediaDialog(true); }}>
 <Edit className="w-4 h-4" />
 </Button>
 <Button size="sm" variant="ghost" onClick={() => deleteMediaAsset(asset.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Ad Campaigns Management */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Ad Campaigns</CardTitle>
 <CardDescription>Manage advertising campaigns and their placement across the platform</CardDescription>
 </div>
 <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingCampaign(null); setNewCampaign({
 name: '', description: '', media_asset_id: '', location: 'dashboard_banner', 
 priority: 'medium', target_user_roles: ['free', 'tier_1', 'tier_2', 'tier_3'], 
 exclude_admin: true, start_date: null, end_date: null, click_url: '', 
 display_order: 0, max_impressions: null, custom_title: '', custom_description: '', custom_cta_text: ''
 }); }} disabled={mediaAssets.length === 0}>
 <Plus className="w-4 h-4 mr-2" />Create Campaign
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-4xl">
 <DialogHeader>
 <DialogTitle>{editingCampaign ? 'Edit' : 'Create'} Ad Campaign</DialogTitle>
 <DialogDescription>Configure campaign settings and targeting options</DialogDescription>
 </DialogHeader>
 <div className="space-y-6">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Campaign Name</Label>
 <Input value={newCampaign.name} onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})} placeholder="Holiday Sale Banner..." />
 </div>
 <div>
 <Label>Media Asset</Label>
 <Select value={newCampaign.media_asset_id} onValueChange={(value) => setNewCampaign({...newCampaign, media_asset_id: value})}>
 <SelectTrigger>
 <SelectValue placeholder="Select media asset" />
 </SelectTrigger>
 <SelectContent>
 {mediaAssets.map(asset => (
 <SelectItem key={asset.id} value={asset.id}>{asset.name} ({asset.media_type})</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 
 <div>
 <Label>Description</Label>
 <Textarea value={newCampaign.description} onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})} />
 </div>
 
 <div className="grid grid-cols-3 gap-4">
 <div>
 <Label>Placement Location</Label>
 <Select value={newCampaign.location} onValueChange={(value) => setNewCampaign({...newCampaign, location: value})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {adLocations.map(location => (
 <SelectItem key={location.value} value={location.value}>
 {location.label}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Priority</Label>
 <Select value={newCampaign.priority} onValueChange={(value) => setNewCampaign({...newCampaign, priority: value})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="low">Low</SelectItem>
 <SelectItem value="medium">Medium</SelectItem>
 <SelectItem value="high">High</SelectItem>
 <SelectItem value="urgent">Urgent</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Display Order</Label>
 <Input type="number" value={newCampaign.display_order} onChange={(e) => setNewCampaign({...newCampaign, display_order: parseInt(e.target.value)})} />
 </div>
 </div>
 
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Start Date (Optional)</Label>
 <Input type="datetime-local" value={newCampaign.start_date || ''} onChange={(e) => setNewCampaign({...newCampaign, start_date: e.target.value})} />
 </div>
 <div>
 <Label>End Date (Optional)</Label>
 <Input type="datetime-local" value={newCampaign.end_date || ''} onChange={(e) => setNewCampaign({...newCampaign, end_date: e.target.value})} />
 </div>
 </div>
 
 <div>
 <Label>Click URL (Optional)</Label>
 <Input value={newCampaign.click_url} onChange={(e) => setNewCampaign({...newCampaign, click_url: e.target.value})} placeholder="https://..." />
 </div>
 
 <div>
 <Label className="text-base font-medium">Target User Roles</Label>
 <div className="flex gap-3 mt-2">
 {['free', 'tier_1', 'tier_2', 'tier_3'].map(role => (
 <div key={role} className="flex items-center gap-2">
 <Switch 
 checked={newCampaign.target_user_roles.includes(role)} 
 onCheckedChange={() => toggleTargetRole(role)} 
 />
 <Label className="capitalize">{role.replace('_', ' ')}</Label>
 </div>
 ))}
 </div>
 </div>
 
 <div className="grid grid-cols-3 gap-4">
 <div>
 <Label>Custom Title (Optional)</Label>
 <Input value={newCampaign.custom_title} onChange={(e) => setNewCampaign({...newCampaign, custom_title: e.target.value})} />
 </div>
 <div>
 <Label>Custom Description (Optional)</Label>
 <Input value={newCampaign.custom_description} onChange={(e) => setNewCampaign({...newCampaign, custom_description: e.target.value})} />
 </div>
 <div>
 <Label>CTA Button Text (Optional)</Label>
 <Input value={newCampaign.custom_cta_text} onChange={(e) => setNewCampaign({...newCampaign, custom_cta_text: e.target.value})} placeholder="Learn More" />
 </div>
 </div>
 
 <div className="flex items-center gap-4">
 <div className="flex items-center gap-2">
 <Switch 
 checked={newCampaign.exclude_admin} 
 onCheckedChange={(checked) => setNewCampaign({...newCampaign, exclude_admin: checked})} 
 />
 <Label>Hide from Admin Users</Label>
 </div>
 <div>
 <Label>Max Impressions (Optional)</Label>
 <Input type="number" value={newCampaign.max_impressions || ''} onChange={(e) => setNewCampaign({...newCampaign, max_impressions: e.target.value ? parseInt(e.target.value) : null})} placeholder="10000" />
 </div>
 </div>
 
 <Button onClick={saveCampaign} className="w-full">Save Campaign</Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {adCampaigns.length === 0 ? (
 <p className="text-center text-slate-500 py-8">No campaigns yet. Create your first campaign to start advertising.</p>
 ) : (
 <div className="space-y-3">
 {adCampaigns.map(campaign => (
 <div key={campaign.id} className="p-4 border rounded">
 <div className="flex justify-between items-start mb-2">
 <div>
 <h4 className="font-medium">{campaign.name}</h4>
 <p className="text-sm text-slate-600">{campaign.location.replace('_', ' ')} • Priority: {campaign.priority}</p>
 <p className="text-xs text-slate-500">{campaign.description}</p>
 </div>
 <div className="flex gap-2">
 <span className={`px-2 py-1 text-xs rounded ${campaign.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
 {campaign.active ? 'Active' : 'Inactive'}
 </span>
 <Button size="sm" variant="ghost" onClick={() => { setEditingCampaign(campaign); setNewCampaign(campaign); setShowCampaignDialog(true); }}>
 <Edit className="w-4 h-4" />
 </Button>
 <Button size="sm" variant="ghost" onClick={() => deleteCampaign(campaign.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 </div>
 </div>
 <div className="text-sm text-slate-600 grid grid-cols-3 gap-4">
 <div>Impressions: {campaign.current_impressions || 0}</div>
 <div>Clicks: {campaign.click_count || 0}</div>
 <div>CTR: {campaign.current_impressions ? ((campaign.click_count / campaign.current_impressions) * 100).toFixed(2) : 0}%</div>
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </TabsContent>

 {/* DESIGN SYSTEM MANAGEMENT */}
 <TabsContent value="design">
 <div className="space-y-6">
 {/* Current Active System */}
 <Card>
 <CardHeader>
 <CardTitle>Active Design System</CardTitle>
 <CardDescription>Currently applied theme and design settings</CardDescription>
 </CardHeader>
 <CardContent>
 {activeDesignSystem ? (
 <div className="flex items-center justify-between p-4 border rounded-lg bg-pro-soft">
 <div>
 <h3 className="font-semibold text-lg">{activeDesignSystem.name}</h3>
 <p className="text-sm text-slate-600">Theme: {activeDesignSystem.theme_preset.replace('_', ' ')}</p>
 <p className="text-xs text-slate-500">Version: {activeDesignSystem.version} • Updated: {new Date(activeDesignSystem.updated_at).toLocaleDateString()}</p>
 </div>
 <div className="flex items-center gap-2">
 <div className="flex gap-1">
 <div className="w-6 h-6 rounded border" style={{backgroundColor: activeDesignSystem.colors?.primary_500}}></div>
 <div className="w-6 h-6 rounded border" style={{backgroundColor: activeDesignSystem.colors?.secondary_500}}></div>
 <div className="w-6 h-6 rounded border" style={{backgroundColor: activeDesignSystem.colors?.success_500}}></div>
 </div>
 <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">ACTIVE</span>
 </div>
 </div>
 ) : (
 <div className="text-center py-8 text-slate-500">No active design system</div>
 )}
 </CardContent>
 </Card>

 {/* Theme Presets */}
 <Card>
 <CardHeader>
 <CardTitle>Theme Presets</CardTitle>
 <CardDescription>Quick start with professionally designed themes</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
 {themePresets.map(preset => (
 <div key={preset.preset_name} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
 <div className="flex gap-1 mb-3">
 <div className="w-8 h-8 rounded border" style={{backgroundColor: preset.colors.primary_500}}></div>
 <div className="w-8 h-8 rounded border" style={{backgroundColor: preset.colors.secondary_500}}></div>
 <div className="w-8 h-8 rounded border" style={{backgroundColor: preset.colors.success_500}}></div>
 </div>
 <h4 className="font-semibold">{preset.display_name}</h4>
 <p className="text-sm text-slate-600 mb-3">{preset.description}</p>
 <Button size="sm" variant="outline" onClick={() => createFromPreset(preset.preset_name)} className="w-full">
 Create Theme
 </Button>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* All Design Systems */}
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Design Systems</CardTitle>
 <CardDescription>Manage all available design systems and themes</CardDescription>
 </div>
 <Dialog open={showDesignDialog} onOpenChange={setShowDesignDialog}>
 <DialogTrigger asChild>
 <Button onClick={() => { setEditingDesignSystem(null); setNewDesignSystem({
 name: '', theme_preset: 'custom', colors: {}, typography: {}, spacing: {}, components: {}, branding: {}
 }); }}>
 <Plus className="w-4 h-4 mr-2" />Create Custom
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-4xl">
 <DialogHeader>
 <DialogTitle>{editingDesignSystem ? 'Edit' : 'Create'} Design System</DialogTitle>
 <DialogDescription>Configure colors, typography, spacing, and branding</DialogDescription>
 </DialogHeader>
 <div className="space-y-6">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>System Name</Label>
 <Input value={newDesignSystem.name} onChange={(e) => setNewDesignSystem({...newDesignSystem, name: e.target.value})} placeholder="My Custom Theme" />
 </div>
 <div>
 <Label>Base Theme</Label>
 <Select value={newDesignSystem.theme_preset} onValueChange={(value) => setNewDesignSystem({...newDesignSystem, theme_preset: value})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="custom">Custom</SelectItem>
 <SelectItem value="glacier_core">Glacier Core</SelectItem>
 <SelectItem value="candy_burst">Candy Burst</SelectItem>
 <SelectItem value="legacy_freeze_tech">Legacy Freeze Tech</SelectItem>
 <SelectItem value="academy_scholar">Academy Scholar</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div>
 <Label className="text-base font-medium">Primary Colors</Label>
 <div className="grid grid-cols-5 gap-3 mt-2">
 {['primary_50', 'primary_100', 'primary_500', 'primary_600', 'primary_700'].map(colorKey => (
 <div key={colorKey}>
 <Label className="text-xs">{colorKey.replace('_', '-')}</Label>
 <Input
 type="color"
 value={newDesignSystem.colors?.[colorKey] || '#0EA5E9'}
 onChange={(e) => setNewDesignSystem({
 ...newDesignSystem,
 colors: { ...newDesignSystem.colors, [colorKey]: e.target.value }
 })}
 className="h-8 p-1"
 />
 </div>
 ))}
 </div>
 </div>

 <div>
 <Label className="text-base font-medium">Secondary Colors</Label>
 <div className="grid grid-cols-5 gap-3 mt-2">
 {['secondary_50', 'secondary_100', 'secondary_500', 'secondary_600', 'secondary_700'].map(colorKey => (
 <div key={colorKey}>
 <Label className="text-xs">{colorKey.replace('_', '-')}</Label>
 <Input
 type="color"
 value={newDesignSystem.colors?.[colorKey] || '#F97316'}
 onChange={(e) => setNewDesignSystem({
 ...newDesignSystem,
 colors: { ...newDesignSystem.colors, [colorKey]: e.target.value }
 })}
 className="h-8 p-1"
 />
 </div>
 ))}
 </div>
 </div>

 <div>
 <Label className="text-base font-medium">Branding</Label>
 <div className="grid grid-cols-2 gap-4 mt-2">
 <div>
 <Label>Brand Name</Label>
 <Input
 value={newDesignSystem.branding?.brand_name || 'Freeze Dry Academy'}
 onChange={(e) => setNewDesignSystem({
 ...newDesignSystem,
 branding: { ...newDesignSystem.branding, brand_name: e.target.value }
 })}
 />
 </div>
 <div>
 <Label>Tagline</Label>
 <Input
 value={newDesignSystem.branding?.tagline || 'Master the Art of Freeze Drying'}
 onChange={(e) => setNewDesignSystem({
 ...newDesignSystem,
 branding: { ...newDesignSystem.branding, tagline: e.target.value }
 })}
 />
 </div>
 </div>
 </div>

 <div className="flex gap-2">
 <Button onClick={saveDesignSystem} className="flex-1">
 <Save className="w-4 h-4 mr-2" />
 Save Design System
 </Button>
 <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}>
 {previewMode ? 'Exit Preview' : 'Preview'}
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </CardHeader>
 <CardContent>
 {designSystems.length === 0 ? (
 <div className="text-center py-8 text-slate-500">
 <Palette className="w-12 h-12 mx-auto mb-4 text-slate-400" />
 <p>No design systems created yet</p>
 <p className="text-sm">Create one from a preset or build a custom theme</p>
 </div>
 ) : (
 <div className="space-y-3">
 {designSystems.map(system => (
 <div key={system.id} className="flex justify-between items-center p-4 border rounded-lg">
 <div className="flex items-center gap-4">
 <div className="flex gap-1">
 <div className="w-6 h-6 rounded border" style={{backgroundColor: system.colors?.primary_500}}></div>
 <div className="w-6 h-6 rounded border" style={{backgroundColor: system.colors?.secondary_500}}></div>
 </div>
 <div>
 <p className="font-medium">{system.name}</p>
 <p className="text-sm text-slate-600">
 {system.theme_preset.replace('_', ' ')} • v{system.version}
 {system.is_active && <span className="text-green-600 font-medium"> • ACTIVE</span>}
 </p>
 </div>
 </div>
 <div className="flex gap-2">
 {!system.is_active && (
 <Button size="sm" variant="outline" onClick={() => activateDesignSystem(system.id)}>
 Activate
 </Button>
 )}
 <Button size="sm" variant="ghost" onClick={() => { setEditingDesignSystem(system); setNewDesignSystem(system); setShowDesignDialog(true); }}>
 <Edit className="w-4 h-4" />
 </Button>
 {!system.is_active && !system.is_default && (
 <Button size="sm" variant="ghost" onClick={() => deleteDesignSystem(system.id)}>
 <Trash2 className="w-4 h-4 text-red-500" />
 </Button>
 )}
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </TabsContent>

 {/* FORMS */}
 <TabsContent value="forms">
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Theme & Design</CardTitle>
 <CardDescription>Customize app appearance</CardDescription>
 </div>
 {settingsModified && (
 <Button onClick={saveSettings}>
 <Save className="w-4 h-4 mr-2" />Save Changes
 </Button>
 )}
 </div>
 </CardHeader>
 <CardContent className="space-y-6">
 {settings && (
 <>
 <div>
 <h3 className="font-semibold mb-3">Branding</h3>
 <div className="space-y-4">
 <div>
 <Label>App Name</Label>
 <Input value={settings.theme?.app_name || ''} onChange={(e) => updateTheme('app_name', e.target.value)} />
 </div>
 <div>
 <Label>Tagline</Label>
 <Input value={settings.theme?.tagline || ''} onChange={(e) => updateTheme('tagline', e.target.value)} />
 </div>
 </div>
 </div>

 <div>
 <h3 className="font-semibold mb-3">Colors</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Primary Color</Label>
 <div className="flex gap-2">
 <Input type="color" value={settings.theme?.primary_color || '#4f46e5'} onChange={(e) => updateTheme('primary_color', e.target.value)} className="w-20" />
 <Input value={settings.theme?.primary_color || '#4f46e5'} onChange={(e) => updateTheme('primary_color', e.target.value)} />
 </div>
 </div>
 <div>
 <Label>Secondary Color</Label>
 <div className="flex gap-2">
 <Input type="color" value={settings.theme?.secondary_color || '#06b6d4'} onChange={(e) => updateTheme('secondary_color', e.target.value)} className="w-20" />
 <Input value={settings.theme?.secondary_color || '#06b6d4'} onChange={(e) => updateTheme('secondary_color', e.target.value)} />
 </div>
 </div>
 </div>
 <p className="text-sm text-slate-500 mt-2">Note: Color changes require app restart to fully apply</p>
 </div>

 <div>
 <h3 className="font-semibold mb-3">Marketing Content</h3>
 <div className="space-y-4">
 <div>
 <Label>Hero Title</Label>
 <Input value={settings.marketing?.hero_title || ''} onChange={(e) => updateMarketing('hero_title', e.target.value)} />
 </div>
 <div>
 <Label>Hero Subtitle</Label>
 <Textarea value={settings.marketing?.hero_subtitle || ''} onChange={(e) => updateMarketing('hero_subtitle', e.target.value)} rows={3} />
 </div>
 <div className="flex items-center gap-2">
 <Switch checked={settings.marketing?.features_visible !== false} onCheckedChange={(checked) => updateMarketing('features_visible', checked)} />
 <Label>Show Feature Cards on Landing</Label>
 </div>
 </div>
 </div>
 </>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 {/* FEATURES */}
 <TabsContent value="features">
 <Card>
 <CardHeader>
 <div className="flex justify-between items-center">
 <div>
 <CardTitle>Feature Toggles</CardTitle>
 <CardDescription>Enable or disable app features</CardDescription>
 </div>
 {settingsModified && (
 <Button onClick={saveSettings}>
 <Save className="w-4 h-4 mr-2" />Save Changes
 </Button>
 )}
 </div>
 </CardHeader>
 <CardContent>
 {settings && (
 <div className="space-y-4">
 {Object.entries(settings.features || {}).map(([feature, enabled]) => (
 <div key={feature} className="flex items-center justify-between p-4 bg-slate-50 rounded">
 <div>
 <p className="font-medium capitalize">{feature.replace(/_/g, ' ')}</p>
 <p className="text-sm text-slate-500">User access to this module</p>
 </div>
 <Switch checked={enabled} onCheckedChange={(checked) => updateFeature(feature, checked)} />
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>
 </div>
 </div>
 );
};

export default AdminPortal;
